import { webApi } from '@mi-front/api-clients';
import { DateTime } from 'luxon';

function isPublicationWithinOneHour(upload: webApi.FragmentSocialUploadItemDto): boolean {
  const publishedAtIso = (() => {
    if (!upload.data || typeof upload.data !== 'object') {
      return undefined;
    }

    const maybePublishedAt = (upload.data as { publishedAt?: unknown }).publishedAt;
    return typeof maybePublishedAt === 'string' ? maybePublishedAt : undefined;
  })();

  if (!publishedAtIso) {
    // If the backend doesn't provide the publication time yet, we can't enforce the 1-hour cap.
    // Keep the previous behavior (keep polling when it should otherwise be polled).
    return true;
  }

  const publishedAt = DateTime.fromISO(publishedAtIso);
  if (!publishedAt.isValid) {
    // Don't block polling if `publishedAt` is provided but can't be parsed.
    return true;
  }

  // diffNow is (publishedAt - now); we want (now - publishedAt) to ensure it's in the past.
  const ageHours = DateTime.now().diff(publishedAt).as('hours');
  return ageHours >= 0 && ageHours <= 1;
}

/** Returns true for statuses that require polling until terminal. */
export function isUploadPending(
  status: webApi.FragmentSocialUploadItemDtoStatusEnumKey | undefined,
): boolean {
  if (!status) {
    return false;
  }

  const pending = webApi.FragmentSocialUploadItemDtoStatusEnum;
  return (
    status === pending.SCHEDULED ||
    status === pending.WAITING ||
    status === pending.WAITING_CHILDREN ||
    status === pending.IN_PROGRESS
  );
}

/**
 * Returns true when the upload should be polled: either status is pending, or status is COMPLETED
 * but the publicUrl is still missing (e.g. backend has not set it yet).
 */
export function shouldPollUpload(upload: webApi.FragmentSocialUploadItemDto | undefined): boolean {
  if (!upload) {
    return false;
  }

  // Hard stop: never poll for more than 1 hour after the publication time.
  // This prevents endless polling when the platform doesn't set `publicUrl` in time.
  if (!isPublicationWithinOneHour(upload)) {
    return false;
  }

  if (isUploadPending(upload.status)) {
    return true;
  }

  if (upload.status === webApi.FragmentSocialUploadItemDtoStatusEnum.COMPLETED) {
    return !upload.platformFeatures?.publicUrl;
  }

  return false;
}

/**
 * Polls a single upload by id from the BFF. Refetches every 5s while status is pending.
 * When used for an upload that was pending at start, keep showing this result (no panorama invalidation).
 * Also enforces a 1-hour max window from publication time.
 */
export function useFragmentSocialUploadPolling(
  uploadId: string | undefined,
  options: { enabled: boolean },
) {
  const { enabled } = options;

  const query = webApi.useGetFragmentSocialUploadsUploadById(uploadId ?? '', {
    query: {
      enabled: enabled && !!uploadId,
      refetchInterval: (q) => {
        const data = q.state.data as webApi.FragmentSocialUploadItemDto | undefined;
        return shouldPollUpload(data) && q.state.dataUpdateCount < 30 ? 5000 : false;
      },
    },
  });

  return query;
}
